import { useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Redirects to /wc2026 after `timeoutMs` of inactivity (no mouse, touch, or keyboard events).
 * Only active when `enabled` is true — pass `isKioskRoute` from MatchDetailPage / TeamPage.
 *
 * Returns a `reset` function — call it from inner scrollable containers whose scroll
 * events don't bubble to window (e.g. overflow-y-scroll divs on iOS).
 */
export function useKioskInactivity({ enabled = true, timeoutMs = 5 * 60 * 1000 } = {}) {
    const navigate = useNavigate();
    const timerRef = useRef(null);

    const reset = useCallback(() => {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
            navigate('/wc2026');
        }, timeoutMs);
    }, [navigate, timeoutMs]);

    useEffect(() => {
        if (!enabled) return;

        const events = ['mousemove', 'mousedown', 'keydown', 'touchstart', 'scroll', 'click'];
        events.forEach(e => window.addEventListener(e, reset, { passive: true }));

        // Start the timer immediately
        reset();

        return () => {
            if (timerRef.current) clearTimeout(timerRef.current);
            events.forEach(e => window.removeEventListener(e, reset));
        };
    }, [enabled, reset]);

    // Return reset so callers can wire it to inner scroll containers
    return reset;
}
